import { forwardRef, useEffect, useRef, useState } from 'react'
import { useComposedRefs } from '../../../../utils/customHooks'
import { SwitchContext } from './components/switch-context'
import { Switch } from './styled-components'
import SwitchThumbComponent from './components/switch-thumb'
import PropTypes, { oneOf } from 'prop-types'
import SwitchSkeletonComponent from './components/switch-skeleton'
import { switchSizeOptions } from './constants'

const SwitchComponent = forwardRef(
  (
    {
      name,
      size = switchSizeOptions.SMALL,
      checked: checkedProp,
      defaultChecked = false,
      required,
      disabled,
      onCheckedChange,
      loading = false,
      ...props
    },
    forwardedRef
  ) => {
    const [button, setButton] = useState(null)
    const composedRefs = useComposedRefs(forwardedRef, (node) =>
      setButton(node)
    )

    const isFormControl = button ? Boolean(button.closest('form')) : true

    const [checkedComponent = false, setCheckedComponent] =
      useState(defaultChecked)

    const setChecked = onCheckedChange ?? setCheckedComponent
    const checked = checkedProp ?? checkedComponent

    const getState = (checked) => {
      return checked ? 'checked' : 'unchecked'
    }

    if (loading) {
      return (
        <SwitchSkeletonComponent size={size} dataState={getState(checked)} />
      )
    }

    return (
      <SwitchContext.Provider value={{ size, checked, disabled, getState }}>
        <Switch
          type="button"
          role="switch"
          aria-checked={checked}
          aria-required={required}
          data-state={getState(checked)}
          data-disabled={disabled ? '' : undefined}
          disabled={disabled}
          value={checked}
          size={size}
          {...props}
          ref={composedRefs}
          onClick={() => {
            setChecked(!checked)
          }}
        >
          <SwitchThumbComponent />
        </Switch>
        {isFormControl && (
          <BubbleInput
            control={button}
            name={name}
            value={checked}
            checked={checked}
            required={required}
            disabled={disabled}
            // We transform because the input is absolutely positioned but we have
            // rendered it **after** the button. This pulls it back to sit on top
            // of the button.
            style={{ transform: 'translateX(-100%)' }}
          />
        )}
      </SwitchContext.Provider>
    )
  }
)

const BubbleInput = (props) => {
  const { checked, ...inputProps } = props
  const ref = useRef(null)
  const prevChecked = useRef(checked)

  useEffect(() => {
    const input = ref.current

    if (prevChecked.current !== checked) {
      input.checked = checked
      prevChecked.current = checked
    }
  }, [checked])

  return (
    <input
      type="checkbox"
      aria-hidden
      defaultChecked={checked}
      {...inputProps}
      tabIndex={-1}
      ref={ref}
      style={{
        ...props.style,
        position: 'absolute',
        pointerEvents: 'none',
        opacity: 0,
        margin: 0
      }}
    />
  )
}

SwitchComponent.propTypes = {
  name: PropTypes.string,
  size: oneOf(Object.values(switchSizeOptions)),
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onCheckedChange: PropTypes.func
}

SwitchComponent.displayName = 'SwitchComponent'
export default SwitchComponent
